import Button from '@/components/Common/Button';
import { DisplayValue } from '@/components/Common/DisplayData';
import LayoutGap from '@/components/Common/LayoutGap';
import Heading, { Size as HeadingSize } from '@/components/Typography/Heading';
import { AlertDanger, AlertWarning } from '@/components/ui/alert';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '@/components/ui/Dialog';
import { OfferLinkResponse } from '@/hooks/api/offerLinks';
import formatSQLTemplateInput from '@/lib/formatSQLTemplateInput';
import { useOfferById } from '@/refactored/hooks/useOffers';
import { useSegment } from '@/refactored/hooks/useSegment';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLinkIcon } from 'lucide-react';
import { IOffer } from '@kardfinancial/models';

const LinkToOffer = ({ offer }: { offer: IOffer }) => (
  <Link
    to={`/offers/edit/${offer?._id}`}
    className="flex items-center gap-1 text-primary-500 hover:underline"
    target="_blank"
  >
    {offer.name}
    <ExternalLinkIcon className="h-3 w-3" />
  </Link>
);

const OfferDetails = ({ offer }: { offer: IOffer }) => (
  <LayoutGap direction="column">
    <DisplayValue
      label={<LinkToOffer offer={offer} />}
      type="id"
      value={offer._id}
    />
    <LayoutGap>
      <DisplayValue label="Offer Status" type="status" value={offer.status} />
      <DisplayValue
        label="Start Date"
        type="datetime"
        value={offer.startDate}
      />
      <DisplayValue
        label="Expiration Date"
        type="datetime"
        value={offer.expirationDate}
      />
    </LayoutGap>
  </LayoutGap>
);

type SegmentDetailsProps = {
  segmentId: string;
};

const SegmentDetails = ({ segmentId }: SegmentDetailsProps) => {
  const [{ data: segment, loading: segmentLoading, error: segmentError }] =
    useSegment(segmentId);

  if (segmentError) {
    return (
      <AlertDanger
        title="Error fetching segment"
        description={segmentError.message}
      />
    );
  }

  if (segmentLoading) {
    return 'Loading...';
  }

  if (!segment) {
    return null;
  }

  return (
    <LayoutGap direction="column">
      <DisplayValue label={segment.name} type="id" value={segment.id} />
      <LayoutGap>
        <DisplayValue
          label="Segment Status"
          type="status"
          value={segment?.status}
        />
        <DisplayValue
          label="Segment Size"
          type="number"
          value={segment?.size}
        />
      </LayoutGap>
    </LayoutGap>
  );
};

type OfferLinkViewProps = {
  offerLink: OfferLinkResponse;
};

// TEMPORARY: This is a temporary adapter. We need to update the API/DB to replace COMPLETED with LINKED.
const offerLinkStatusMap: Record<
  OfferLinkResponse['attributes']['status'],
  string
> = {
  DRAFT: 'DRAFT',
  COMPLETED: 'LINKED',
};

const OfferLinkView = ({ offerLink }: OfferLinkViewProps) => {
  const [{ data: offer, loading: offerLoading, error: offerError }] =
    useOfferById(offerLink.attributes.offerId);

  const [parsedRules, parsedRulesError] = useMemo(() => {
    if (!offerLink.attributes.rules) {
      return null;
    }

    const parsed = formatSQLTemplateInput({
      merchantName: '',
      merchantId: (offerLink.attributes.rules as any).merchantId,
      offerIds: [offerLink.attributes.offerId],
      segmentDescription: '',
      experimentId: offerLink.attributes.experimentId,
      isIncrementality: true,
      isOnlyAlbertNetNewUsers: false,
      userDateRange: {
        from: '',
        to: '',
      },
      ...(offerLink.attributes.rules as any),
    });
    return [JSON.stringify(parsed.data, null, 2), parsed.error];
  }, [offerLink]);

  if (offerError) {
    return (
      <AlertDanger
        title="Error fetching offer"
        description={offerError.message}
      />
    );
  }

  if (offerLoading) {
    return 'Loading...';
  }

  const { attributes, createdAt, updatedAt } = offerLink;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="secondary" size="small">
          Offer link details
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Offer link details</DialogTitle>
        </DialogHeader>
        <LayoutGap direction="column">
          <LayoutGap>
            <DisplayValue
              label="Status"
              type="status"
              value={offerLinkStatusMap[attributes.status] || attributes.status}
            />
            <DisplayValue
              label="Created At"
              type="datetime"
              value={createdAt}
            />
            <DisplayValue
              label="Updated At"
              type="datetime"
              value={updatedAt}
            />
          </LayoutGap>
          <DisplayValue
            label="SQL Template Input"
            type="code"
            value={parsedRules || 'No rules'}
          />
          {parsedRulesError && (
            <AlertWarning
              title="SQL Template Input is invalid"
              description="Something might not be right with the JSON generated for this offer link."
            />
          )}
          {offer && (
            <>
              <hr />
              <Heading size={HeadingSize.XS} className="m-0">
                Linked Offer
              </Heading>
              <OfferDetails offer={offer} />
            </>
          )}
          {offer?.segments?.[0] && (
            <>
              <hr />
              <Heading size={HeadingSize.XS} className="m-0">
                Test Group Segment
              </Heading>
              <SegmentDetails segmentId={offer.segments[0]} />
            </>
          )}
        </LayoutGap>
      </DialogContent>
    </Dialog>
  );
};

export default OfferLinkView;
